import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Generic methods for dynamic endpoints
  get: (url) => axios.get(`${process.env.REACT_APP_BACKEND_URL}${url}`, { headers: getHeaders() }),
  post: (url, data) => axios.post(`${process.env.REACT_APP_BACKEND_URL}${url}`, data, { headers: getHeaders() }),
  
  // Dashboard
  getDashboardMetrics: () => 
    axios.get(`${API_URL}/dashboard/metrics`, { headers: getHeaders() }),
  
  getChartData: (period = '7d') => 
    axios.get(`${API_URL}/dashboard/chart-data?period=${period}`, { headers: getHeaders() }),

  // Clients
  getClients: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/clients${query ? `?${query}` : ''}`, { headers: getHeaders() });
  },
  
  createClient: (data) => 
    axios.post(`${API_URL}/clients`, data, { headers: getHeaders() }),
  
  getClient: (id) => 
    axios.get(`${API_URL}/clients/${id}`, { headers: getHeaders() }),
  
  updateKycStatus: (id, status) => 
    axios.patch(`${API_URL}/clients/${id}/kyc?status=${status}`, {}, { headers: getHeaders() }),

  // Positions
  getPositions: (clientId = null) => {
    const query = clientId ? `?client_id=${clientId}` : '';
    return axios.get(`${API_URL}/positions${query}`, { headers: getHeaders() });
  },

  // Trades
  getTrades: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/trades${query ? `?${query}` : ''}`, { headers: getHeaders() });
  },

  executeTrade: (order) =>
    axios.post(`${API_URL}/trades/execute`, order, { headers: getHeaders() }),

  closeTrade: (tradeId) =>
    axios.post(`${API_URL}/trades/${tradeId}/close`, {}, { headers: getHeaders() }),

  // Audit Logs
  getAuditLogs: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return axios.get(`${API_URL}/audit-logs${query ? `?${query}` : ''}`, { headers: getHeaders() });
  },

  // Settings
  getSettings: () => 
    axios.get(`${API_URL}/settings`, { headers: getHeaders() }),
  
  updateSettings: (data) => 
    axios.patch(`${API_URL}/settings`, data, { headers: getHeaders() }),

  // MT5 - individual methods (legacy)
  getMT5Status: () =>
    axios.get(`${API_URL}/mt5/status`, { headers: getHeaders() }),

  testMT5Connection: () =>
    axios.post(`${API_URL}/mt5/test-connection`, {}, { headers: getHeaders() }),

  getMT5Prices: () =>
    axios.get(`${API_URL}/mt5/prices`, { headers: getHeaders() }),
  
  // MT5 - grouped namespace
  mt5: {
    getStatus: () => axios.get(`${API_URL}/mt5/status`, { headers: getHeaders() }),
    testConnection: () => axios.post(`${API_URL}/mt5/test-connection`, {}, { headers: getHeaders() }),
    getPrices: () => axios.get(`${API_URL}/mt5/prices`, { headers: getHeaders() }),
  },

  // Copy Trading
  getCopyProviders: (activeOnly = true) =>
    axios.get(`${API_URL}/copy-trading/providers?active_only=${activeOnly}`, { headers: getHeaders() }),

  createSignalProvider: (data) =>
    axios.post(`${API_URL}/copy-trading/providers`, data, { headers: getHeaders() }),

  getSignalProvider: (providerId) =>
    axios.get(`${API_URL}/copy-trading/providers/${providerId}`, { headers: getHeaders() }),

  followProvider: (data) =>
    axios.post(`${API_URL}/copy-trading/follow`, data, { headers: getHeaders() }),

  getMySubscriptions: () =>
    axios.get(`${API_URL}/copy-trading/subscriptions`, { headers: getHeaders() }),

  unfollowProvider: (subscriptionId) =>
    axios.delete(`${API_URL}/copy-trading/subscriptions/${subscriptionId}`, { headers: getHeaders() }),

  // IB Portal
  registerAsIB: (data) =>
    axios.post(`${API_URL}/ib/register`, data, { headers: getHeaders() }),

  getIBProfile: () =>
    axios.get(`${API_URL}/ib/profile`, { headers: getHeaders() }),

  getIBReferrals: () =>
    axios.get(`${API_URL}/ib/referrals`, { headers: getHeaders() }),

  getIBDashboard: () =>
    axios.get(`${API_URL}/ib/dashboard`, { headers: getHeaders() }),

  requestPayout: (data) =>
    axios.post(`${API_URL}/ib/payout`, data, { headers: getHeaders() }),

  // Payments
  getDepositPackages: () =>
    axios.get(`${API_URL}/payments/packages`, { headers: getHeaders() }),

  createDeposit: (data) =>
    axios.post(`${API_URL}/payments/deposit`, data, { headers: getHeaders() }),

  getPaymentStatus: (sessionId) =>
    axios.get(`${API_URL}/payments/status/${sessionId}`, { headers: getHeaders() }),

  getPaymentHistory: () =>
    axios.get(`${API_URL}/payments/history`, { headers: getHeaders() }),
};

export default api;
